import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Package, Clock, Truck, Home as HomeIcon, XCircle, CheckCircle2, Search } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useFmt } from "../context/CurrencyContext";

const STATUS_META = {
  pending: { label: "Pending", icon: Clock, color: "#F59E0B" },
  processing: { label: "Processing", icon: Package, color: "#3B82F6" },
  processing_complete: { label: "Ready to ship", icon: CheckCircle2, color: "#8B5CF6" },
  in_transit: { label: "In transit", icon: Truck, color: "#06B6D4" },
  delivered: { label: "Delivered", icon: HomeIcon, color: "#10B981" },
  cancelled: { label: "Cancelled", icon: XCircle, color: "#EF4444" },
};

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fmtJMD = useFmt();

  const lookup = async (e) => {
    e.preventDefault();
    setError(""); setOrder(null);
    if (!orderId.trim() || !email.trim()) {
      setError("Please enter both your order ID and the email used to place it.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/orders/track/${orderId.trim()}`, { params: { email: email.trim() } }
      );
      setOrder(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Order not found. Please check your email and order ID.");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-6 py-12 sm:py-16" data-testid="track-order-page">
      <div className="overline text-zinc-500 mb-2">Track your order</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
        Where's my box?
      </h1>
      <p className="text-zinc-600 mb-8 text-sm sm:text-base">
        Enter your order ID and the email you used at checkout — no account needed.
      </p>

      <form onSubmit={lookup} className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6 space-y-3" data-testid="track-form">
        <Field label="Order ID">
          <input value={orderId} onChange={(e) => setOrderId(e.target.value)}
                 placeholder="e.g. 3f2c-…"
                 className="input font-mono" data-testid="track-order-id" />
        </Field>
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="you@example.com"
                 className="input" data-testid="track-email" />
        </Field>
        {error && <div className="text-sm text-red-600" data-testid="track-error">{error}</div>}
        <button type="submit" disabled={loading}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="track-submit">
          <Search size={14} /> {loading ? "Looking up…" : "Track order"}
        </button>
      </form>

      {order && <OrderCard order={order} />}

      <div className="mt-8 text-center text-sm text-zinc-500">
        Have an account? <Link to="/login" className="text-[#F74B64] font-semibold">Sign in</Link> to see all your orders.
      </div>

      <style>{`.input { width:100%; border:1px solid #e4e4e7; border-radius:0.5rem; padding:0.5rem 0.75rem; font-size:0.875rem; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-600 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function OrderCard({ order }) {
  const meta = STATUS_META[order.status] || STATUS_META.pending;
  const StepIcon = meta.icon;
  const flowKeys = ["pending", "processing", "processing_complete", "in_transit", "delivered"];
  const fmtJMD = useFmt();
  return (
    <div className="mt-8 rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6" data-testid="tracked-order">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-xs text-zinc-500 font-mono">#{order.id.slice(0, 8)}</div>
          <div className="font-display font-bold text-xl mt-0.5">
            {order.customer_name} · {fmtJMD(order.total)}
          </div>
        </div>
        <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full"
             style={{ background: meta.color + "22", color: meta.color }}>
          <StepIcon size={12} /> {meta.label}
        </div>
      </div>
      <div className="text-sm text-zinc-500 mb-4">
        {order.parish} · {order.delivery_day} · {order.items.length} item{order.items.length !== 1 ? "s" : ""}
      </div>
      {order.status !== "cancelled" ? (
        <div className="flex gap-1 mb-4">
          {flowKeys.map((s) => {
            const done = flowKeys.indexOf(order.status) >= flowKeys.indexOf(s);
            return <div key={s} className="flex-1 h-1 rounded-full"
                        style={{ background: done ? "#F74B64" : "#E4E4E7" }} />;
          })}
        </div>
      ) : (
        <div className="p-3 rounded-lg bg-red-50 text-sm text-red-700 mb-4">
          Cancelled{order.cancelled_reason ? ` — ${order.cancelled_reason}` : ""}
        </div>
      )}
      {order.status_history && order.status_history.length > 0 && (
        <div className="text-xs text-zinc-500 space-y-1">
          {order.status_history.map((h, i) => (
            <div key={i}>
              {h.status} · {new Date(h.timestamp).toLocaleString()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
